import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PoChartSerie, PoChartType, PoNotificationService, PoSelectOption } from '@po-ui/ng-components';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { ConsolidatedSummary } from '../../interfaces/consolidated-report';
import { DailyReportListResult, DailyReportRow } from '../../interfaces/reports';
import { SonarExtractorService } from '../../services/sonar-extractor.service';
import {
  buildCircularDailySeries,
  buildDailyChartData,
  buildDefaultEnd,
  buildDefaultStart,
  clearOrphanTooltips,
  exportChartToPng,
  formatDateTime,
  isCircularChartType,
  SONAR_CHART_TYPE_OPTIONS_NO_CIRCULAR,
  startTooltipObserver,
} from '../../utils/sonar-extractor.utils';

/** Quantidade de dias do período padrão do gráfico de evolução diária */
const DASHBOARD_DEFAULT_PERIOD_DAYS = 5;

@Component({
  selector: "app-sonar-extractor-dashboard",
  templateUrl: "./sonar-extractor-dashboard.component.html",
  styleUrls: ["./sonar-extractor-dashboard.component.css"],
  
})
export class SonarExtractorDashboardComponent implements OnInit, OnDestroy {
  loading = false;

  // Flag de loading para botão de download
  downloadingLineChart = false;

  // Opções de tipo de gráfico (provenientes das utils centralizadas)
  readonly chartOptionsDailyEvolution: PoSelectOption[] = SONAR_CHART_TYPE_OPTIONS_NO_CIRCULAR;

  // KPIs
  totalIssues       = 0;
  totalResolve        = 0;
  totalResolveOrReduce = 0;
  releaseName = '';
  kpiLastUpdated = '';

  // MutationObserver: remove tooltips órfãos do body ao detectar novos
  private tooltipObserver: MutationObserver | null = null;

  // Flag para forçar recriação do po-chart ao trocar tipo (evita acúmulo de tooltips)
  showLineChart = true;

  // Seletor de tipo de gráfico
  private _lineChartType: PoChartType = PoChartType.Bar;
  get lineChartType(): PoChartType { return this._lineChartType; }
  set lineChartType(value: PoChartType) {
    this._lineChartType = value;
    this.resetChart();
  }

  /** Força recriação do gráfico de linha e limpa tooltips órfãos */
  private resetChart(): void {
    clearOrphanTooltips();
    this.showLineChart = false;
    setTimeout(() => (this.showLineChart = true), 0);
  }

  // Gráfico de linha (diário)
  lineChartCategories: string[] = [];
  lineChartSeries: PoChartSerie[] = [];

  // Dados brutos para recalcular séries circulares (Pie/Donut)
  private _dailyRaw: DailyReportRow[] = [];

  // ─── Getters: adapta séries ao tipo de gráfico selecionado ───────────────────

  get lineChartDisplaySeries(): PoChartSerie[] {
    return isCircularChartType(this.lineChartType)
      ? buildCircularDailySeries(this._dailyRaw)
      : this.lineChartSeries;
  }

  get lineChartDisplayCategories(): string[] {
    return isCircularChartType(this.lineChartType) ? [] : this.lineChartCategories;
  }

  get hasLineChartData(): boolean { return this._dailyRaw.length > 0; }

  // Filtro de período para o gráfico diário
  dailyPeriod: { start: string; end: string } = {
    start: buildDefaultStart(DASHBOARD_DEFAULT_PERIOD_DAYS),
    end:   buildDefaultEnd(),
  };

  private readonly dailyPeriod$ = new Subject<{ startDate: string; endDate: string }>();

  constructor(
    private sonarService: SonarExtractorService,
    private notification: PoNotificationService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.tooltipObserver = startTooltipObserver();
    this.dailyPeriod$
      .pipe(
        debounceTime(300),
        distinctUntilChanged((a, b) => a.startDate === b.startDate && a.endDate === b.endDate),
        switchMap(filters => this.sonarService.getReportDaily(filters)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next:  data => this.applyDailyChartData(data),
        error: ()   => this.notification.error('Erro ao carregar o relatório diário.'),
      });

    // Recarrega dashboard quando uma extração é concluída
    this.sonarService.extractionCompleted$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshDashboard());

    this.loadConsolidated();
    this.triggerDailyLoad();
  }

  ngOnDestroy(): void {
    this.tooltipObserver?.disconnect();
    clearOrphanTooltips();
  }

  /** Recarrega todos os dados do dashboard (KPIs + gráfico diário) */
  refreshDashboard(): void {
    this.loadConsolidated();
    this.loadDailyDirect();
    this.notification.information('Dashboard atualizado com os dados da última extração.');
  }

  /** Carrega o gráfico diário diretamente, ignorando o distinctUntilChanged do pipeline */
  private loadDailyDirect(): void {
    if (!this.dailyPeriod.start || !this.dailyPeriod.end) return;
    this.sonarService
      .getReportDaily({ startDate: this.dailyPeriod.start, endDate: this.dailyPeriod.end })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next:  data => this.applyDailyChartData(data),
        error: ()   => this.notification.error('Erro ao carregar o relatório diário.'),
      });
  }

  private loadConsolidated(): void {
    this.loading = true;
    this.sonarService.getConsolidatedSummary()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: summary => {
          this.releaseName = summary.release_name;
          this.applyKPIs(summary);
          this.loading = false;
        },
        error: () => {
          this.notification.error('Erro ao carregar os totalizadores.');
          this.loading = false;
        },
      });
  }

  loadDaily(): void {
    this.triggerDailyLoad();
  }

  onDailyPeriodChange(): void {
    this.triggerDailyLoad();
  }

  private triggerDailyLoad(): void {
    if (!this.dailyPeriod.start || !this.dailyPeriod.end) return;
    this.dailyPeriod$.next({
      startDate: this.dailyPeriod.start,
      endDate:   this.dailyPeriod.end,
    });
  }

  downloadLineChartAsPng(): void {
    this.downloadingLineChart = true;
    exportChartToPng('dashboard-line-chart', 'evolucao-diaria.png', msg => this.notification.warning(msg))
      .finally(() => (this.downloadingLineChart = false));
  }

  private applyKPIs(summary: ConsolidatedSummary): void {
    this.totalIssues       = summary.issues_total;
    this.totalResolve        = summary.resolve;
    this.totalResolveOrReduce = summary.resolve_or_reduce;
    this.kpiLastUpdated = formatDateTime(summary.last_run);
  }

  // ─── Builder de gráfico (delega para utils centralizadas) ──────────────────

  private applyDailyChartData(result: DailyReportListResult): void {
    const rows = result.rows ?? [];
    this._dailyRaw = rows;
    const { categories, series } = buildDailyChartData(rows);
    this.lineChartCategories = categories;
    this.lineChartSeries     = series;
  }
}

